/* iter55k — Admin panel for Premier Studio human services.
 *
 * Three tabs:
 *   1. Availability — create/list/delete bookable coaching slots.
 *   2. Coaching queue — see + patch all coaching sessions.
 *   3. Editor reviews — see + patch queue, write summary letters,
 *      manage inline paragraph comments.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { humanServicesAdminApi, adminApi, editorialOrdersAdminApi } from '@/lib/api';
import { openConfirm } from '@/components/admin/ConfirmDialog';
import { toast } from 'sonner';
import EditorialRatesPanel from './EditorialRatesPanel';
import {
  Users, ScrollText, Calendar, Plus, Trash2, Loader2, Save, MessageSquarePlus, Pencil,
  ClipboardList, CreditCard, RefreshCw, ChevronDown,
} from 'lucide-react';

function fmtTime(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      weekday: 'short', month: 'short', day: 'numeric',
      hour: 'numeric', minute: '2-digit',
    });
  } catch { return iso; }
}

export default function HumanServicesAdminPanel() {
  // iter55q — scope-aware tab visibility. Coach = coaching-only,
  // Editor = reviews-only, pro_writer = everything.
  const [scopes, setScopes] = useState(null);
  useEffect(() => {
    let cancelled = false;
    adminApi.me().then(r => {
      if (!cancelled) setScopes(r?.scopes || []);
    }).catch(() => { if (!cancelled) setScopes([]); });
    return () => { cancelled = true; };
  }, []);

  const isFull  = scopes === null || scopes.includes('pro_writer');
  const isCoach = isFull || (scopes || []).includes('coach');
  const isEditor = isFull || (scopes || []).includes('editor');

  // Land on the first tab the current role has access to.
  const defaultTab = isFull ? 'availability'
    : isCoach ? 'coaching'
    : isEditor ? 'reviews'
    : 'coaching';

  const roleLabel = isFull
    ? 'Full admin — all queues'
    : (isCoach && isEditor) ? 'Coach + Editor'
    : isCoach ? 'Coach role — coaching queue only'
    : isEditor ? 'Editor role — editor reviews only'
    : 'No human-services access';

  return (
    <div className="max-w-5xl space-y-4" data-testid="admin-human-services-panel">
      <header>
        <h2 className="text-[15px] font-semibold inline-flex items-center gap-2">
          <Users className="h-4 w-4 text-primary" /> Human Services (Premier Studio)
        </h2>
        <p className="text-[11.5px] text-muted-foreground mt-1">
          Manage coaching availability and the editor-review queue for
          Premier Studio subscribers. Each user gets 1 coaching call +
          1 editor review per calendar month.
        </p>
        <p className="text-[10.5px] text-primary mt-1 font-mono-stat" data-testid="hs-role-label">
          {roleLabel}
        </p>
      </header>

      <Tabs defaultValue={defaultTab} key={defaultTab}>
        <TabsList className="h-8">
          {isFull && (
            <TabsTrigger value="availability" className="h-7 text-[11.5px]" data-testid="hs-tab-availability">
              <Calendar className="mr-1 h-3 w-3" /> Availability
            </TabsTrigger>
          )}
          {isCoach && (
            <TabsTrigger value="coaching" className="h-7 text-[11.5px]" data-testid="hs-tab-coaching">
              <Users className="mr-1 h-3 w-3" /> Coaching Queue
            </TabsTrigger>
          )}
          {isEditor && (
            <TabsTrigger value="reviews" className="h-7 text-[11.5px]" data-testid="hs-tab-reviews">
              <ScrollText className="mr-1 h-3 w-3" /> Editor Reviews
            </TabsTrigger>
          )}
          {isFull && (
            <TabsTrigger value="rate-table" className="h-7 text-[11.5px]" data-testid="hs-tab-rate-table">
              <ScrollText className="mr-1 h-3 w-3" /> Rate Table
            </TabsTrigger>
          )}
          {isFull && (
            <TabsTrigger value="editorial-orders" className="h-7 text-[11.5px]" data-testid="hs-tab-editorial-orders">
              <ClipboardList className="mr-1 h-3 w-3" /> Editorial Orders
            </TabsTrigger>
          )}
        </TabsList>

        {isFull  && <TabsContent value="availability" className="m-0 mt-3"><AvailabilityTab /></TabsContent>}
        {isCoach && <TabsContent value="coaching"     className="m-0 mt-3"><CoachingQueueTab /></TabsContent>}
        {isEditor && <TabsContent value="reviews"      className="m-0 mt-3"><EditorReviewsTab /></TabsContent>}
        {isFull  && <TabsContent value="rate-table"   className="m-0 mt-3"><EditorialRatesPanel /></TabsContent>}
        {isFull  && <TabsContent value="editorial-orders" className="m-0 mt-3"><EditorialOrdersTab /></TabsContent>}
      </Tabs>
    </div>
  );
}

// ---- Availability -------------------------------------------------

function AvailabilityTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ start: '', end: '', notes: '' });
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    humanServicesAdminApi.listAvailability(true)
      .then((r) => setRows(r.slots || []))
      .catch(() => toast.error('Failed to load slots'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!form.start || !form.end) {
      toast.error('Start and end times are required.');
      return;
    }
    setBusy(true);
    try {
      const toIso = (v) => new Date(v).toISOString();
      await humanServicesAdminApi.createSlot({
        start_ts: toIso(form.start),
        end_ts:   toIso(form.end),
        notes:    form.notes || null,
      });
      setForm({ start: '', end: '', notes: '' });
      toast.success('Slot created.');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Create failed.');
    } finally {
      setBusy(false);
    }
  };

  const del = async (id) => {
    if (!(await openConfirm({ title: 'Delete this slot?', tone: 'destructive' }))) return;
    try {
      await humanServicesAdminApi.deleteSlot(id);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed.');
    }
  };

  return (
    <div className="space-y-4">
      <section className="rounded-md border border-border bg-card p-3">
        <div className="text-[11.5px] font-semibold">Open a new slot</div>
        <div className="mt-2 grid gap-2 md:grid-cols-4">
          <div>
            <Label className="text-[10.5px]">Start (local)</Label>
            <Input type="datetime-local"
                   value={form.start}
                   onChange={(e) => setForm({ ...form, start: e.target.value })}
                   className="h-8 text-[11.5px]"
                   data-testid="hs-slot-start-input" />
          </div>
          <div>
            <Label className="text-[10.5px]">End (local)</Label>
            <Input type="datetime-local"
                   value={form.end}
                   onChange={(e) => setForm({ ...form, end: e.target.value })}
                   className="h-8 text-[11.5px]"
                   data-testid="hs-slot-end-input" />
          </div>
          <div className="md:col-span-2">
            <Label className="text-[10.5px]">Notes (optional)</Label>
            <Input value={form.notes}
                   onChange={(e) => setForm({ ...form, notes: e.target.value })}
                   className="h-8 text-[11.5px]"
                   placeholder="e.g. Structural editing focus"
                   data-testid="hs-slot-notes-input" />
          </div>
        </div>
        <Button size="sm" onClick={submit} disabled={busy}
                className="mt-2 h-7 text-[11.5px]"
                data-testid="hs-slot-create-btn">
          {busy ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <Plus className="mr-1 h-3 w-3" />}
          Create slot
        </Button>
      </section>

      <section>
        <div className="text-[10.5px] uppercase tracking-wider text-muted-foreground mb-1.5">
          All slots · {rows.length}
        </div>
        {loading ? (
          <div className="text-[11.5px] text-muted-foreground">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="text-[11.5px] text-muted-foreground">No slots yet.</div>
        ) : (
          <div className="space-y-1" data-testid="hs-slot-list">
            {rows.map((s) => (
              <div key={s.id}
                   data-testid={`hs-slot-row-${s.id}`}
                   className="flex items-center justify-between rounded-md border border-border bg-background px-3 py-1.5 text-[12px]">
                <div className="min-w-0">
                  <div>{fmtTime(s.start_ts)} → {fmtTime(s.end_ts)} <span className="text-muted-foreground">({s.duration_minutes} min)</span></div>
                  {s.notes && <div className="text-[10.5px] text-muted-foreground truncate">{s.notes}</div>}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {s.taken_by
                    ? <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-[9.5px] font-medium text-emerald-800">Booked</span>
                    : <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[9.5px] font-medium text-slate-700">Open</span>}
                  {!s.taken_by && (
                    <Button size="sm" variant="ghost"
                            onClick={() => del(s.id)}
                            className="h-6 px-1 text-destructive"
                            data-testid={`hs-slot-delete-${s.id}`}>
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

// ---- Coaching queue -----------------------------------------------

const COACHING_STATUSES = ['pending', 'confirmed', 'completed', 'cancelled'];

function CoachingQueueTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = useCallback(() => {
    setLoading(true);
    humanServicesAdminApi.listCoaching()
      .then((r) => setRows(r.sessions || []))
      .catch(() => toast.error('Failed to load coaching queue'))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const patch = async (id, status) => {
    try {
      await humanServicesAdminApi.patchCoaching(id, { status });
      toast.success(`Session ${status}.`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Update failed.');
    }
  };

  if (loading) return <div className="text-[11.5px] text-muted-foreground">Loading…</div>;
  if (rows.length === 0) return <div className="text-[11.5px] text-muted-foreground">No coaching sessions yet.</div>;

  return (
    <div className="space-y-1.5" data-testid="hs-coaching-queue">
      {rows.map((r) => (
        <div key={r.id}
             data-testid={`hs-coaching-row-${r.id}`}
             className="rounded-md border border-border bg-background p-3 text-[12px]">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="font-medium">{fmtTime(r.start_ts) || '(no slot info)'}</div>
              <div className="text-[10.5px] text-muted-foreground">
                {r.month_key} · user {r.user_id?.slice(0, 8)}…
              </div>
              {r.notes && (
                <div className="mt-1 rounded bg-secondary/50 p-1.5 text-[11.5px]">
                  <span className="text-muted-foreground">User note:</span> {r.notes}
                </div>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-1 shrink-0">
              <StatusBadge status={r.status} />
              {COACHING_STATUSES.map((s) => (
                <Button key={s} size="sm" variant="outline"
                        className="h-6 px-1.5 text-[10px]"
                        disabled={s === r.status}
                        onClick={() => patch(r.id, s)}
                        data-testid={`hs-coaching-${r.id}-set-${s}`}>
                  {s}
                </Button>
              ))}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ---- Editor reviews -----------------------------------------------

const REVIEW_STATUSES = ['requested', 'in_progress', 'completed', 'cancelled'];

function EditorReviewsTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const load = useCallback(() => {
    setLoading(true);
    humanServicesAdminApi.listReviews()
      .then((r) => setRows(r.reviews || []))
      .catch(() => toast.error('Failed to load reviews'))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="text-[11.5px] text-muted-foreground">Loading…</div>;
  if (rows.length === 0) return <div className="text-[11.5px] text-muted-foreground">No editor reviews yet.</div>;

  if (editingId) {
    return <ReviewEditor rid={editingId}
                          onBack={() => { setEditingId(null); load(); }} />;
  }

  return (
    <div className="space-y-1.5" data-testid="hs-reviews-queue">
      {rows.map((r) => (
        <div key={r.id}
             data-testid={`hs-review-row-${r.id}`}
             className="flex items-center justify-between rounded-md border border-border bg-background p-3 text-[12px]">
          <div className="min-w-0">
            <div className="font-medium">{r.project_name || 'Untitled'}</div>
            <div className="text-[10.5px] text-muted-foreground">
              {r.month_key} · requested {fmtTime(r.requested_at)}
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <StatusBadge status={r.status} />
            <Button size="sm" variant="outline"
                    onClick={() => setEditingId(r.id)}
                    className="h-6 px-2 text-[10.5px]"
                    data-testid={`hs-review-open-${r.id}`}>
              <Pencil className="mr-1 h-3 w-3" /> Open
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
}

function ReviewEditor({ rid, onBack }) {
  const [review, setReview]     = useState(null);
  const [comments, setComments] = useState([]);
  const [letter, setLetter]     = useState('');
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      humanServicesAdminApi.listReviews(),
      humanServicesAdminApi.listComments(rid),
    ]).then(([r, c]) => {
      const found = (r.reviews || []).find((x) => x.id === rid);
      setReview(found || null);
      setLetter(found?.summary_letter || '');
      setComments(c.comments || []);
    }).catch(() => toast.error('Failed to load review'))
      .finally(() => setLoading(false));
  }, [rid]);

  useEffect(() => { load(); }, [load]);

  const saveReview = async (patch) => {
    setSaving(true);
    try {
      const r = await humanServicesAdminApi.patchReview(rid, patch);
      setReview(r.review);
      toast.success('Saved.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="text-[11.5px] text-muted-foreground">Loading review…</div>;
  if (!review) return <div className="text-[11.5px] text-muted-foreground">Review not found.</div>;

  return (
    <div className="space-y-4" data-testid="hs-review-editor">
      <header className="flex items-center justify-between">
        <div>
          <Button size="sm" variant="ghost" onClick={onBack} className="h-6 px-2 text-[11px]"
                  data-testid="hs-review-back-btn">
            ← Back to queue
          </Button>
          <h3 className="mt-1 text-[13px] font-semibold">
            {review.project_name || 'Untitled'} <span className="text-muted-foreground text-[11px]">· {review.month_key}</span>
          </h3>
        </div>
        <div className="flex items-center gap-1">
          <StatusBadge status={review.status} />
          {REVIEW_STATUSES.filter((s) => s !== review.status).map((s) => (
            <Button key={s} size="sm" variant="outline"
                    className="h-6 px-1.5 text-[10px]"
                    onClick={() => saveReview({ status: s })}
                    disabled={saving}
                    data-testid={`hs-review-set-${s}`}>
              {s}
            </Button>
          ))}
        </div>
      </header>

      <section className="rounded-md border border-border bg-card p-3">
        <Label className="text-[11px] font-semibold">Summary letter</Label>
        <Textarea value={letter}
                  onChange={(e) => setLetter(e.target.value)}
                  className="mt-1 min-h-[140px] font-serif text-[12.5px]"
                  placeholder="Dear author, …"
                  maxLength={20000}
                  data-testid="hs-review-summary-input" />
        <Button size="sm" onClick={() => saveReview({ summary_letter: letter })}
                disabled={saving}
                className="mt-2 h-7 text-[11.5px]"
                data-testid="hs-review-save-summary-btn">
          {saving ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <Save className="mr-1 h-3 w-3" />}
          Save summary letter
        </Button>
      </section>

      <CommentsEditor rid={rid} comments={comments} onChanged={load} />
    </div>
  );
}

function CommentsEditor({ rid, comments, onChanged }) {
  const [form, setForm] = useState({ chapter_id: '', paragraph_index: 0, text: '' });
  const [busy, setBusy] = useState(false);

  const add = async () => {
    if (!form.chapter_id || !form.text) {
      toast.error('Chapter and text are required.');
      return;
    }
    setBusy(true);
    try {
      await humanServicesAdminApi.addComment(rid, {
        chapter_id: form.chapter_id.trim(),
        paragraph_index: Number(form.paragraph_index) || 0,
        text: form.text.trim(),
      });
      setForm({ chapter_id: '', paragraph_index: 0, text: '' });
      toast.success('Comment added.');
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Add failed.');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (cid) => {
    if (!(await openConfirm({ title: 'Delete this comment?', tone: 'destructive' }))) return;
    try {
      await humanServicesAdminApi.deleteComment(cid);
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed.');
    }
  };

  return (
    <section className="rounded-md border border-border bg-card p-3">
      <div className="flex items-center justify-between">
        <Label className="text-[11px] font-semibold">Inline comments · {comments.length}</Label>
      </div>

      <div className="mt-2 grid gap-2 md:grid-cols-4">
        <Input placeholder="Chapter id"
               value={form.chapter_id}
               onChange={(e) => setForm({ ...form, chapter_id: e.target.value })}
               className="h-8 text-[11.5px]"
               data-testid="hs-comment-chapter-input" />
        <Input type="number" min={0}
               placeholder="Paragraph #"
               value={form.paragraph_index}
               onChange={(e) => setForm({ ...form, paragraph_index: e.target.value })}
               className="h-8 text-[11.5px]"
               data-testid="hs-comment-para-input" />
        <Input placeholder="Comment text…"
               value={form.text}
               onChange={(e) => setForm({ ...form, text: e.target.value })}
               className="md:col-span-2 h-8 text-[11.5px]"
               data-testid="hs-comment-text-input" />
      </div>
      <Button size="sm" onClick={add} disabled={busy}
              className="mt-2 h-7 text-[11.5px]"
              data-testid="hs-comment-add-btn">
        <MessageSquarePlus className="mr-1 h-3 w-3" /> Add comment
      </Button>

      <ul className="mt-3 space-y-1">
        {comments.map((c) => (
          <li key={c.id}
              data-testid={`hs-comment-${c.id}`}
              className="flex items-start justify-between gap-2 rounded border border-border bg-background px-2 py-1.5 text-[11.5px]">
            <div className="min-w-0">
              <div className="text-[10px] text-muted-foreground">Ch {c.chapter_id} · ¶ {c.paragraph_index + 1}</div>
              <div className="whitespace-pre-wrap">{c.text}</div>
            </div>
            <Button size="sm" variant="ghost"
                    onClick={() => remove(c.id)}
                    className="h-6 px-1 text-destructive"
                    data-testid={`hs-comment-delete-${c.id}`}>
              <Trash2 className="h-3 w-3" />
            </Button>
          </li>
        ))}
      </ul>
    </section>
  );
}

function StatusBadge({ status }) {
  const map = {
    pending:     'bg-slate-100 text-slate-700',
    confirmed:   'bg-blue-100 text-blue-800',
    completed:   'bg-emerald-100 text-emerald-800',
    cancelled:   'bg-rose-100 text-rose-800',
    requested:   'bg-slate-100 text-slate-700',
    in_progress: 'bg-amber-100 text-amber-800',
  };
  return (
    <span className={`rounded px-1.5 py-0.5 text-[9.5px] font-medium ${map[status] || 'bg-slate-100 text-slate-700'}`}>
      {status?.replace('_', ' ')}
    </span>
  );
}


// ──────────────────────────────────────────────────────────────────────────────
// Editorial Orders Tab — Phase 3 Workflow Tracking
// ──────────────────────────────────────────────────────────────────────────────

const STAGE_LABELS = {
  proofread:     'Proofreading',
  copyedit:      'Copyediting',
  developmental: 'Developmental Edit',
  full_pipeline: 'Full Editorial Pipeline',
};

const STATUS_OPTIONS = [
  { value: 'all', label: 'All statuses' },
  { value: 'pending',     label: 'Pending' },
  { value: 'paid',        label: 'Paid' },
  { value: 'in_review',   label: 'In Review' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'delivered',   label: 'Delivered' },
  { value: 'complete',    label: 'Complete' },
  { value: 'cancelled',   label: 'Cancelled' },
];

// Status machine: what transitions are valid from each status
const NEXT_STATUSES = {
  pending:     ['paid', 'cancelled'],
  paid:        ['in_review', 'cancelled'],
  in_review:   ['in_progress', 'cancelled'],
  in_progress: ['delivered', 'cancelled'],
  delivered:   ['complete', 'in_progress'],
  complete:    [],
  cancelled:   [],
  failed:      ['pending', 'cancelled'],
};

const STATUS_COLORS = {
  pending:     'bg-yellow-100 text-yellow-800',
  paid:        'bg-blue-100 text-blue-800',
  in_review:   'bg-purple-100 text-purple-800',
  in_progress: 'bg-indigo-100 text-indigo-800',
  delivered:   'bg-emerald-100 text-emerald-800',
  complete:    'bg-green-100 text-green-800',
  cancelled:   'bg-slate-100 text-slate-600',
  failed:      'bg-red-100 text-red-700',
};

const fmtUSD = (n) => n != null ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n) : '—';
const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }); }
  catch { return iso; }
};

function EditorialOrdersTab() {
  const [orders,      setOrders]      = useState([]);
  const [editors,     setEditors]     = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [statusFilter,setStatusFilter]= useState('');
  const [expandedId,  setExpandedId]  = useState(null);
  const [assigning,   setAssigning]   = useState({});
  const [transitioning,setTransitioning] = useState({});
  const [payouts,     setPayouts]     = useState([]);
  const [showPayouts, setShowPayouts] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [od, ed] = await Promise.all([
        editorialOrdersAdminApi.listOrders(statusFilter && statusFilter !== 'all' ? statusFilter : undefined),
        editorialOrdersAdminApi.listEditors(),
      ]);
      setOrders(od.orders || []);
      setEditors(Array.isArray(ed) ? ed : (ed.editors || []));
    } catch {
      toast.error('Failed to load editorial orders');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const loadPayouts = async () => {
    try {
      const r = await editorialOrdersAdminApi.listPayouts();
      setPayouts(r.payouts || []);
      setShowPayouts(true);
    } catch { toast.error('Failed to load payouts'); }
  };

  const assignEditor = async (orderId, editorId) => {
    if (!editorId) return;
    setAssigning(a => ({ ...a, [orderId]: true }));
    try {
      await editorialOrdersAdminApi.assignEditor(orderId, editorId);
      toast.success('Editor assigned');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Assignment failed');
    } finally {
      setAssigning(a => ({ ...a, [orderId]: false }));
    }
  };

  const transitionStatus = async (orderId, newStatus) => {
    setTransitioning(t => ({ ...t, [orderId]: true }));
    try {
      await editorialOrdersAdminApi.transitionStatus(orderId, newStatus);
      toast.success(`Status → ${newStatus.replace('_', ' ')}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Transition failed');
    } finally {
      setTransitioning(t => ({ ...t, [orderId]: false }));
    }
  };

  return (
    <div className="space-y-3" data-testid="admin-editorial-orders-tab">
      {/* Toolbar */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Select value={statusFilter || 'all'} onValueChange={v => setStatusFilter(v === 'all' ? '' : v)}>
            <SelectTrigger className="h-7 w-36 text-[11.5px]" data-testid="eo-status-filter">
              <SelectValue placeholder="All statuses" />
            </SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.map(o => (
                <SelectItem key={o.value} value={o.value} className="text-[12px]">{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button size="sm" variant="outline" className="h-7 text-[11.5px] gap-1" onClick={load}>
            <RefreshCw className="h-3 w-3" /> Refresh
          </Button>
        </div>
        <Button size="sm" variant="outline" className="h-7 text-[11.5px] gap-1" onClick={loadPayouts}>
          <CreditCard className="h-3 w-3" /> View Payouts
        </Button>
      </div>

      {/* Payouts panel */}
      {showPayouts && (
        <div className="rounded-md border bg-card p-3 space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-[12px] font-semibold">Payout Records ({payouts.length})</p>
            <Button size="sm" variant="ghost" className="h-6 text-[11px]" onClick={() => setShowPayouts(false)}>Hide</Button>
          </div>
          {payouts.length === 0 ? (
            <p className="text-[12px] text-muted-foreground">No payouts yet.</p>
          ) : (
            <table className="w-full text-[11.5px]">
              <thead><tr className="text-muted-foreground border-b">
                <th className="pb-1 text-left">Order</th>
                <th className="pb-1 text-left">Editor</th>
                <th className="pb-1 text-right">Gross</th>
                <th className="pb-1 text-right">Platform (20%)</th>
                <th className="pb-1 text-right">Editor (80%)</th>
                <th className="pb-1 text-left">Date</th>
              </tr></thead>
              <tbody>
                {payouts.map(p => (
                  <tr key={p.id} className="border-b last:border-0">
                    <td className="py-1 font-mono text-[10px]">{p.order_id?.slice(-8)}</td>
                    <td className="py-1">{p.editor_name || p.editor_id || '—'}</td>
                    <td className="py-1 text-right">{fmtUSD(p.gross_usd)}</td>
                    <td className="py-1 text-right text-rose-700">{fmtUSD(p.platform_fee)}</td>
                    <td className="py-1 text-right text-emerald-700 font-semibold">{fmtUSD(p.editor_payout_usd)}</td>
                    <td className="py-1 text-muted-foreground">{fmtDate(p.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Orders list */}
      {loading ? (
        <div className="flex items-center gap-2 py-8 text-muted-foreground justify-center">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading orders…
        </div>
      ) : orders.length === 0 ? (
        <p className="text-center py-8 text-[12px] text-muted-foreground">No orders found.</p>
      ) : (
        <div className="space-y-2">
          {orders.map((o) => {
            const isExpanded  = expandedId === o.id;
            const nextStatus  = NEXT_STATUSES[o.status] || [];
            const isAssigning = assigning[o.id];
            const isTrans     = transitioning[o.id];

            return (
              <div key={o.id} className="rounded-md border bg-card" data-testid={`eo-order-row-${o.id}`}>
                {/* Summary row */}
                <div
                  className="flex items-center gap-3 p-3 cursor-pointer hover:bg-accent/40 transition-colors"
                  onClick={() => setExpandedId(isExpanded ? null : o.id)}
                >
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${STATUS_COLORS[o.status] || 'bg-slate-100'}`}>
                    {o.status.replace('_', ' ').toUpperCase()}
                  </span>
                  <span className="text-[12px] font-semibold flex-1">
                    {STAGE_LABELS[o.stage] || o.stage}
                    {o.specialist ? ' · Specialist' : ''}
                  </span>
                  <span className="text-[12px] text-muted-foreground">{(o.word_count || 0).toLocaleString()} wds</span>
                  <span className="text-[13px] font-bold text-rose-700">{fmtUSD(o.quoted_usd)}</span>
                  <span className="text-[11px] text-muted-foreground">{fmtDate(o.created_at)}</span>
                  <ChevronDown className={`h-3.5 w-3.5 text-muted-foreground transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                </div>

                {/* Expanded detail */}
                {isExpanded && (
                  <div className="border-t p-3 space-y-3">
                    {/* Order details */}
                    <div className="grid grid-cols-3 gap-2 text-[12px]">
                      <div><span className="text-muted-foreground">Order ID</span><br /><span className="font-mono text-[11px]">{o.id}</span></div>
                      <div><span className="text-muted-foreground">User</span><br />{o.user_id}</div>
                      <div><span className="text-muted-foreground">Urgency</span><br />{o.urgency}</div>
                      <div><span className="text-muted-foreground">Type</span><br />{(o.manuscript_type || '').replace('_', ' ')}</div>
                      <div><span className="text-muted-foreground">Platform fee</span><br className="" /><span className="text-rose-600">{fmtUSD(o.platform_fee)}</span></div>
                      <div><span className="text-muted-foreground">Editor payout</span><br /><span className="text-emerald-600">{fmtUSD(o.editor_payout)}</span></div>
                      {o.notes && <div className="col-span-3"><span className="text-muted-foreground">Author notes</span><br />{o.notes}</div>}
                      {o.assigned_editor_name && (
                        <div className="col-span-3"><span className="text-muted-foreground">Assigned editor</span><br />{o.assigned_editor_name}</div>
                      )}
                    </div>

                    {/* Status history */}
                    {(o.status_history || []).length > 0 && (
                      <div>
                        <p className="text-[11px] font-semibold text-muted-foreground mb-1">Status history</p>
                        <div className="space-y-0.5">
                          {o.status_history.map((h, i) => (
                            <div key={i} className="text-[11px] text-muted-foreground flex gap-2">
                              <span className="w-36 truncate">{fmtDate(h.at)}</span>
                              <span>{h.from} → {h.to}</span>
                              <span className="text-muted-foreground/60">{h.actor}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Admin controls */}
                    <div className="flex flex-wrap gap-2">
                      {/* Assign editor */}
                      {o.status !== 'complete' && o.status !== 'cancelled' && editors.length > 0 && (
                        <div className="flex items-center gap-1">
                          <Select
                            value={o.assigned_editor_id || ''}
                            onValueChange={(eid) => assignEditor(o.id, eid)}
                          >
                            <SelectTrigger className="h-7 w-44 text-[11.5px]" data-testid={`eo-assign-editor-${o.id}`}>
                              <SelectValue placeholder="Assign editor…" />
                            </SelectTrigger>
                            <SelectContent>
                              {editors.map(e => (
                                <SelectItem key={e.id} value={e.id} className="text-[12px]">{e.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {isAssigning && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                        </div>
                      )}

                      {/* Status transitions */}
                      {nextStatus.map(ns => (
                        <Button
                          key={ns}
                          size="sm"
                          variant={ns === 'cancelled' ? 'destructive' : 'outline'}
                          className="h-7 text-[11.5px]"
                          disabled={isTrans}
                          onClick={() => transitionStatus(o.id, ns)}
                          data-testid={`eo-transition-${o.id}-${ns}`}
                        >
                          {isTrans && ns === nextStatus[0] && <Loader2 className="h-3 w-3 animate-spin mr-1" />}
                          → {ns.replace('_', ' ')}
                        </Button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
